import React from "react";
import { SkeletonAvatar, SkeletonBlock, SkeletonButton, SkeletonLine, SkeletonPill } from "./Skeleton";

export function AnnouncementRowsSkeleton({ rows = 5, showBullet = false, className = '' }) {
  return (
    <div className={className} role="status" aria-live="polite" aria-label="お知らせを読み込み中">
      {Array.from({ length: rows }, (_, index) => (
        <div key={`announcement-row-skeleton-${index}`} className="announcement-row" aria-hidden="true">
          {showBullet ? <SkeletonLine className="announcement-row__bullet" width={6} height={14} /> : null}
          <div className="announcement-row__content">
            <SkeletonLine width={index === 1 ? '58%' : '82%'} height={15} />
            <SkeletonLine width={112} height={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EventCalendarRowsSkeleton({ rows = 3 }) {
  return (
    <div className="event-list" role="status" aria-live="polite" aria-label="イベントを読み込み中">
      {Array.from({ length: rows }, (_, index) => (
        <div key={`calendar-event-skeleton-${index}`} className="event-item event-item--button" aria-hidden="true">
          <SkeletonLine className="event-item__title" width={index === 1 ? '64%' : '82%'} height={12} />
          <div className="event-item__meta">
            <SkeletonLine width={72} height={12} />
            <SkeletonLine width={index === 2 ? 88 : 118} height={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ArtistMembershipRowsSkeleton({ rows = 3 }) {
  return (
    <div className="artist-membership-list" role="status" aria-live="polite" aria-label="メンバーを読み込み中">
      {Array.from({ length: rows }, (_, index) => (
        <div key={`artist-membership-skeleton-${index}`} className="artist-membership-item" aria-hidden="true">
          <SkeletonAvatar className="artist-membership-item__avatar" size={46} />
          <div className="artist-membership-item__meta">
            <div className="artist-membership-item__name-row">
              <SkeletonLine width={index === 1 ? 96 : 128} height={14} />
              <SkeletonPill width={64} height={21} />
            </div>
            <SkeletonLine width={index === 2 ? 132 : 188} height={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function MaterialHistoryRowsSkeleton({ rows = 4 }) {
  return (
    <div className="eme-modal-list" role="status" aria-live="polite" aria-label="履歴を読み込み中">
      {Array.from({ length: rows }, (_, index) => (
        <div key={`material-history-skeleton-${index}`} className="eme-modal-row" aria-hidden="true">
          <SkeletonLine width={index === 0 ? 52 : 68} height={13} />
          <SkeletonLine width={index === 2 ? '58%' : '82%'} height={13} />
          <SkeletonLine width={118} height={11} />
        </div>
      ))}
    </div>
  );
}

export function MetricCardGridSkeleton({
  count = 3,
  gridClassName,
  cardClassName,
  showProgress = false,
}) {
  return (
    <div className={gridClassName} role="status" aria-live="polite" aria-label="集計を読み込み中">
      {Array.from({ length: count }, (_, index) => (
        <div key={`metric-card-skeleton-${index}`} className={cardClassName} aria-hidden="true">
          <SkeletonLine width={index === 1 ? '52%' : '40%'} height={12} />
          <SkeletonLine width={index === 2 ? '68%' : '56%'} height={20} />
          {showProgress && index === 0 ? <SkeletonLine width="100%" height={8} /> : null}
          {showProgress || index === count - 1 ? <SkeletonLine width="72%" height={11} /> : null}
        </div>
      ))}
    </div>
  );
}

export function TaskCommentRowsSkeleton({ rows = 3, classPrefix = 'task-detail' }) {
  return (
    <ul
      className={`${classPrefix}__commentList`}
      role="status"
      aria-live="polite"
      aria-label="コメントを読み込み中"
    >
      {Array.from({ length: rows }, (_, index) => (
        <li key={`task-comment-skeleton-${index}`} className={`${classPrefix}__commentItem`} aria-hidden="true">
          <div className={`${classPrefix}__commentHead`}>
            <div className={`${classPrefix}__commentAuthor`}>
              <SkeletonAvatar size={20} />
              <SkeletonLine width={index === 1 ? 72 : 96} height={12} />
            </div>
            <SkeletonLine width={104} height={11} />
          </div>
          <SkeletonLine width={index === 2 ? '68%' : '92%'} height={13} />
          <div className={`${classPrefix}__commentReactions`}>
            <SkeletonPill width={42} height={24} />
            <SkeletonPill width={42} height={24} />
            <SkeletonPill width={42} height={24} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function TaskAttachmentRowsSkeleton({ rows = 2, classPrefix = 'task-detail' }) {
  return (
    <ul
      className={`${classPrefix}__attachmentList`}
      role="status"
      aria-live="polite"
      aria-label="添付ファイルを読み込み中"
    >
      {Array.from({ length: rows }, (_, index) => (
        <li key={`task-attachment-skeleton-${index}`} aria-hidden="true">
          <div className={`${classPrefix}__attachmentMeta`}>
            <SkeletonLine width={index === 1 ? '58%' : '76%'} height={13} />
            <SkeletonLine width="68%" height={11} />
          </div>
          <div className={`${classPrefix}__attachmentActions`}>
            <SkeletonButton width={88} height={32} />
            <SkeletonButton width={52} height={32} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function TaskDependencyGridSkeleton({ classPrefix = 'task-detail' }) {
  return (
    <div
      className={`${classPrefix}__dependencyGrid`}
      role="status"
      aria-live="polite"
      aria-label="依存関係を読み込み中"
    >
      {['先行タスク', '後続タスク'].map((label, sectionIndex) => (
        <section key={label} aria-hidden="true">
          <h5>{label}</h5>
          <ul className={`${classPrefix}__dependencyList`}>
            <li>
              <div className={`${classPrefix}__dependencyJump`}>
                <div className={`${classPrefix}__dependencyJumpTitle`}>
                  <SkeletonPill width={52} height={20} />
                  <SkeletonLine width={sectionIndex === 0 ? '66%' : '78%'} height={13} />
                </div>
                <SkeletonLine width={48} height={11} />
              </div>
              <SkeletonButton width={52} height={32} />
            </li>
          </ul>
        </section>
      ))}
    </div>
  );
}

export function ActionRowsSkeleton({
  rows = 3,
  containerClassName = '',
  itemClassName = '',
  mainClassName = '',
  actionsClassName = '',
  actionCount = 1,
  containerAs: Container = 'div',
  itemAs: Item = 'div',
}) {
  return (
    <Container
      className={containerClassName}
      role="status"
      aria-live="polite"
      aria-label="一覧を読み込み中"
    >
      {Array.from({ length: rows }, (_, index) => (
        <Item key={`action-row-skeleton-${index}`} className={itemClassName} aria-hidden="true">
          <div className={mainClassName}>
            <SkeletonLine width={index === 1 ? '58%' : '78%'} height={14} />
            <SkeletonLine width={index === 2 ? '44%' : '62%'} height={11} />
          </div>
          {actionCount > 0 ? (
            <div className={actionsClassName}>
              {Array.from({ length: actionCount }, (_, actionIndex) => (
                <SkeletonButton
                  key={`action-row-skeleton-${index}-action-${actionIndex}`}
                  width={actionIndex === 0 ? 64 : 52}
                  height={32}
                />
              ))}
            </div>
          ) : null}
        </Item>
      ))}
    </Container>
  );
}

export function CastingInfoSkeleton({ rows = 3 }) {
  return (
    <div role="status" aria-live="polite" aria-label="キャスティング情報を読み込み中">
      <div className="castinginfo-progress" aria-hidden="true">
        <div className="castinginfo-progress__header">
          <SkeletonLine width={112} height={12} />
          <SkeletonLine width={72} height={12} />
        </div>
        <SkeletonLine width="100%" height={8} />
      </div>
      <div className="castinginfo-list" aria-hidden="true">
        {Array.from({ length: rows }, (_, index) => (
          <div key={`casting-info-skeleton-${index}`} className="castinginfo-row">
            <div className="castinginfo-row__top">
              <SkeletonLine width={index === 1 ? '44%' : '62%'} height={14} />
              <SkeletonLine width={64} height={12} />
            </div>
            <SkeletonLine width="100%" height={8} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function MusicLibraryRowsSkeleton({ rows = 6, actionCount = 3 }) {
  return (
    <div className="music-library-list" role="status" aria-live="polite" aria-label="楽曲一覧を読み込み中">
      {Array.from({ length: rows }, (_, index) => (
        <div key={`music-library-skeleton-${index}`} className="music-library-list__row" aria-hidden="true">
          <div className="music-library-list__item-main">
            <SkeletonLine width={index === 1 ? '48%' : '72%'} height={14} />
            <SkeletonLine width={index === 3 ? '38%' : '58%'} height={12} />
            <div className="music-library-list__chips">
              <SkeletonPill width={64} height={22} />
              <SkeletonPill width={84} height={22} />
            </div>
          </div>
          <div className="music-library-row-actions">
            {Array.from({ length: actionCount }, (_, actionIndex) => (
              <SkeletonButton
                key={`music-library-skeleton-${index}-action-${actionIndex}`}
                width={actionIndex === 1 ? 88 : 72}
                height={32}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function OfferHistoryCardsSkeleton({ rows = 4, className = 'history-card-list' }) {
  return (
    <div className={className} role="status" aria-live="polite" aria-label="オファー履歴を読み込み中">
      {Array.from({ length: rows }, (_, index) => (
        <article key={`offer-history-card-skeleton-${index}`} className="history-card" aria-hidden="true">
          <SkeletonLine width={112} height={12} />
          <div className="history-card__section">
            <SkeletonLine width={index === 1 ? '66%' : '86%'} height={14} />
            <SkeletonLine width="52%" height={12} />
          </div>
          <div className="history-card__meta-grid">
            <div className="history-card__meta">
              <SkeletonLine width={48} height={11} />
              <SkeletonLine width="72%" height={13} />
            </div>
            <div className="history-card__meta">
              <SkeletonLine width={48} height={11} />
              <SkeletonLine width="64%" height={13} />
            </div>
          </div>
          <SkeletonPill width="100%" height={34} />
        </article>
      ))}
    </div>
  );
}

export function TimetableViewerSkeleton({ rows = 5, columns = 4 }) {
  return (
    <div className="timetable-standalone-wrap" role="status" aria-live="polite" aria-label="タイムテーブルを読み込み中">
      <div className="timetable-standalone-scroll">
        <div className="timetable-standalone-scroll-inner">
          <table className="timetable-standalone-table" aria-hidden="true">
            <thead>
              <tr>
                {Array.from({ length: columns }, (_, index) => (
                  <th key={`viewer-head-skeleton-${index}`}><SkeletonLine width="68%" height={12} /></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rows }, (_, rowIndex) => (
                <tr key={`viewer-row-skeleton-${rowIndex}`}>
                  {Array.from({ length: columns }, (_, columnIndex) => (
                    <td key={`viewer-cell-skeleton-${rowIndex}-${columnIndex}`}>
                      <SkeletonLine width={columnIndex === 0 ? 52 : `${58 + ((rowIndex + columnIndex) % 3) * 12}%`} height={12} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function EventTaskOverviewSkeleton() {
  return (
    <div className="event-task-panel__summary" role="status" aria-live="polite" aria-label="イベントタスクを読み込み中">
      <SkeletonPill width={88} height={22} />
      <SkeletonLine width="48%" height={18} />
      <div className="event-task-panel__counts" aria-hidden="true">
        <SkeletonPill width={72} height={24} />
        <SkeletonPill width={72} height={24} />
        <SkeletonPill width={72} height={24} />
      </div>
      <div className="event-task-panel__visualGrid" aria-hidden="true">
        <SkeletonBlock height={132} radius={12} />
        <SkeletonBlock height={132} radius={12} />
      </div>
    </div>
  );
}

export function EventTaskFocusRowsSkeleton({ rows = 3 }) {
  return (
    <>
      <div className="event-task-panel__focusListHead" aria-hidden="true">
        <span>完了</span><span>タスク名</span><span>担当者</span><span>期限</span>
      </div>
      <ul className="event-task-panel__focusList" role="status" aria-live="polite" aria-label="注目タスクを読み込み中">
        {Array.from({ length: rows }, (_, index) => (
          <li key={`focus-task-skeleton-${index}`} className="event-task-panel__focusItem" aria-hidden="true">
            <div className="event-task-panel__focusRow">
              <SkeletonPill width={28} height={28} />
              <SkeletonLine width={index === 1 ? '58%' : '76%'} height={13} />
              <SkeletonLine width={72} height={12} />
              <SkeletonLine width={64} height={12} />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

export function ManualNavRowsSkeleton({ rows = 5 }) {
  return (
    <nav className="manuals-nav" role="status" aria-live="polite" aria-label="マニュアル一覧を読み込み中">
      <div aria-hidden="true">
        <div className="manuals-nav__group-title"><SkeletonLine width={88} height={12} /></div>
        {Array.from({ length: rows }, (_, index) => (
          <div key={`manual-nav-skeleton-${index}`} className="manuals-nav__item">
            <div className="manuals-nav__item-title">
              <SkeletonLine width={index === 1 ? '58%' : '82%'} height={14} />
            </div>
            <div className="manuals-nav__item-meta">
              <SkeletonLine width={index === 3 ? '38%' : '54%'} height={11} />
            </div>
          </div>
        ))}
      </div>
    </nav>
  );
}
